import { forwardRef } from 'react';
import * as React from 'react';
import type {
  ButtonSizeOptions,
  ButtonStylesMap,
  ButtonSubmit,
  ButtonVariantOptions,
} from '@hotjar/webapp/components/Button/button';
import {
  defaultStyle,
  primaryStyle,
  dangerStyle,
  cleanStyle,
  secondaryStyle,
  lineStyle,
  loadingStyle,
  sizeStyle,
  iconStyle,
  linkStyle,
} from './styles';

export interface ButtonProps {
  variant?: ButtonVariantOptions;
  disabled: boolean;
  children: React.ReactNode;
  secondary: boolean;
  size?: ButtonSizeOptions;
  type: ButtonSubmit;
  // Allow any other passed property
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [anyOtherProp: string]: any;
}

const styleMap: ButtonStylesMap = {
  default: defaultStyle,
  primary: primaryStyle,
  danger: dangerStyle,
  clean: cleanStyle,
  line: lineStyle,
  icon: iconStyle,
  loading: loadingStyle,
  link: linkStyle,
};

/**
 * @deprecated use hotsauce
 * [`<Button />`](https://storybook.hotjar.com/?path=/docs/components-button--basic-button) instead
 * @see Button
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      disabled = false,
      children,
      secondary = false,
      size,
      type = 'button',
      ...otherProps
    },
    ref,
  ) => {
    // Always use the defaultStyle as a base for all button variants.
    const styles = [defaultStyle];

    if (variant && styleMap[variant]) {
      // Extend the base defaultStyle with the variant styles.
      styles.push(styleMap[variant]);
    }

    if (secondary) {
      // Override some of the buttons if it is a secondary button.
      styles.push(secondaryStyle);
    }

    if (size && sizeStyle[size]) {
      // Add button sizing styles if we have a size prop.
      styles.push(sizeStyle[size]);
    }

    return (
      // ESLint does not recognize default prop value, hence the suppression
      // eslint-disable-next-line react/button-has-type
      <button css={styles} disabled={disabled} type={type} ref={ref} {...otherProps}>
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
